// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, Navigate, useBlocker, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserByPublicIdQuery } from "@/api/hooks/use-users";
import { AccessDenied } from "@/components/shared/access-denied";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserBoPermissionsTab, type UserBoPermissionsTabHandle } from "@/features/iam/user-bo-permissions-tab";
import { UserEditTab, type UserEditTabHandle } from "@/features/iam/user-edit-tab";
import { UserPermissionsTab, type UserPermissionsTabHandle } from "@/features/iam/user-permissions-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

const EDIT_TABS = ["general", "permissions", "bo-permissions"] as const;

export const Route = createFileRoute("/_authenticated/iam/users/$publicId/edit")({
  component: UserEditPage,
});

function UserEditPage() {
  const { publicId } = Route.useParams();
  const { t: tab } = Route.useSearch();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");
  const { data } = useUserByPublicIdQuery(publicId);

  const [isEditDirty, setIsEditDirty] = useState(false);
  const [isPermissionsDirty, setIsPermissionsDirty] = useState(false);
  const [isBoPermissionsDirty, setIsBoPermissionsDirty] = useState(false);
  const isFormDirty = isEditDirty || isPermissionsDirty || isBoPermissionsDirty;
  const editTabRef = useRef<UserEditTabHandle>(null);
  const permissionsTabRef = useRef<UserPermissionsTabHandle>(null);
  const boPermissionsTabRef = useRef<UserBoPermissionsTabHandle>(null);

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  function handleTabChange(tab: string) {
    navigate({
      to: "/iam/users/$publicId/edit",
      params: { publicId },
      search: tab === "general" ? {} : { t: tab },
      replace: true,
    });
  }

  if (!hasPermission(PAGE_PERMISSIONS.iamUsersEdit)) return <AccessDenied permission={PAGE_PERMISSIONS.iamUsersEdit} />;
  // A deleted user is read-only — send a stale link back to the read page.
  if (data?.user.isDeleted) return <Navigate to="/iam/users/$publicId" params={{ publicId }} search={{}} replace />;
  const activeTab = EDIT_TABS.find((name) => name === tab) ?? "general";

  return (
    <div>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
        <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
          <TabsList>
            <TabsIndicator />
            <TabsTrigger value="general">{t("tabs.general")}</TabsTrigger>
            <TabsTrigger value="permissions">{t("tabs.permissions")}</TabsTrigger>
            <TabsTrigger value="bo-permissions">{t("tabs.boPermissions")}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="general" className="space-y-4">
          <UserEditTab ref={editTabRef} publicId={publicId} onDirtyChange={setIsEditDirty} />
        </TabsContent>
        <TabsContent value="permissions" className="space-y-4">
          <UserPermissionsTab ref={permissionsTabRef} publicId={publicId} onDirtyChange={setIsPermissionsDirty} />
        </TabsContent>
        <TabsContent value="bo-permissions" className="space-y-4">
          <UserBoPermissionsTab ref={boPermissionsTabRef} publicId={publicId} onDirtyChange={setIsBoPermissionsDirty} />
        </TabsContent>
      </Tabs>

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          permissionsTabRef.current?.reset();
          boPermissionsTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
